import { FC, PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { removeAdminPanelSession, setAdminPanelSession } from 'app/api/cookie';
import { useGetIsTokenExpired, useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { useAuthState, useIsNoaIbex } from 'app/state/AuthContext/hooks';
import { buildLoginLink } from 'app/utils/lib/routing';
import { isTriumphInternal } from 'app/utils/lib/user';

import MissingDetailsValidator from './MissingDetailsValidator';

// Local Typings
interface Props {
  adminOnly?: boolean;
}

export const returnUrlSessionStorageKey = 'returnUrl';

// Component Definition
const AuthRoute: FC<PropsWithChildren<Props>> = ({
  adminOnly,
  children,
}) => {
  const location = useLocation();

  const { authRoles } = useAuthState();
  const isTokenExpired = useGetIsTokenExpired();
  const isNoaProcessorOnly = useIsNoaIbex();

  const user = useGetUser();
  const isAuthenticated = !!(!isTokenExpired && user);

  let returnUrl = buildLoginLink(location);

  if (!isAuthenticated && returnUrl.includes('LoadPayOauth')) {
    // handle LoadPay Oauth URLs in a session so users don't bookmark tokens
    sessionStorage.setItem(returnUrlSessionStorageKey, returnUrl);
    returnUrl = '/';
  }

  if (adminOnly && (!isTriumphInternal(authRoles) && !isNoaProcessorOnly)) {
    if (isAuthenticated) {
      removeAdminPanelSession();
    }

    return <Navigate to={isAuthenticated ? '/' : returnUrl} />;
  }

  if (adminOnly) {
    setAdminPanelSession();
  } else {
    removeAdminPanelSession();
  }

  return isAuthenticated
    ? (
      <MissingDetailsValidator returnUrl={returnUrl}>
        {children}
      </MissingDetailsValidator>
    )
    : <Navigate to={returnUrl} />;
};

export default AuthRoute;
